/* src/features/5DD/BioSummary.js */
import React, { useState, useEffect } from 'react';
import { db } from '../../apiClient';
import { differenceInYears } from 'date-fns';

const styles = {
  container: {
    backgroundColor: 'rgba(20, 20, 20, 0.9)', 
    padding: '15px', borderRadius: '12px',
    marginBottom: '20px', border: '1px solid #444',
    color: '#fff', boxShadow: '0 4px 6px rgba(0,0,0,0.5)',
    maxWidth: '100%', boxSizing: 'border-box'
  },
  summaryRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  bioText: { fontSize: '1.1em', fontWeight: '500' },
  highlight: { color: '#ffd700', fontWeight: 'bold' },
  
  btnEdit: {
    background: 'transparent', border: '1px solid #777', color: '#ccc',
    padding: '5px 12px', borderRadius: '20px', fontSize: '0.8em', cursor: 'pointer'
  },
  btnSave: {
    backgroundColor: '#28a745', color: '#fff', border: 'none',
    padding: '12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', 
    marginTop: '15px', width: '100%', fontSize: '1em'
  },
  formStack: {
    display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px',
    paddingTop: '15px', borderTop: '1px solid #444'
  },
  inputGroup: { display: 'flex', flexDirection: 'column', width: '100%' },
  label: { fontSize: '0.8em', color: '#aaa', marginBottom: '5px' },
  input: {
    padding: '10px', backgroundColor: '#333', border: '1px solid #555',
    color: '#fff', borderRadius: '4px', fontSize: '1em', width: '100%', 
    boxSizing: 'border-box' 
  }
};

function BioSummary({ session, activeCourseId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Form & Display State
  const [formData, setFormData] = useState({
    weight: '', height: '', birthday: '', gender: 'male'
  });
  const [displayTDEE, setDisplayTDEE] = useState(2000);
  const [displayWeight, setDisplayWeight] = useState('--');

  // --- 1. THE FIX: Fetch Fresh Data on Mount ---
  useEffect(() => {
    async function fetchFreshData() {
        if (!session?.user?.id) return;


        try {
            // Fetch Profile
            const profileData = await db.profiles.me();

            // Fetch Course Weight
            let courseWeight = '';
            if (activeCourseId) {
                const courseData = await db.courses.active().catch(() => null);
                if (courseData) courseWeight = courseData.current_weight;
            }

            if (profileData) {
                setFormData({
                    weight: courseWeight || '',
                    height: profileData.height || '',
                    birthday: profileData.birthday || '',
                    gender: profileData.gender || 'male'
                });
                setDisplayTDEE(profileData.target_calories || 2000);
                setDisplayWeight(courseWeight || '--');
            }
        } catch (err) {
            console.error("Error fetching bio data:", err);
        }
    }

    fetchFreshData();
  }, [session, activeCourseId]); // Runs when component mounts or course changes

  const calculateTDEE = (w, h, birthday, g) => {
      if (!w || !h || !birthday) return 2000; 
      const age = differenceInYears(new Date(), new Date(birthday));
      const kg = w / 2.20462;
      const cm = h * 2.54;
      let bmr = (10 * kg) + (6.25 * cm) - (5 * age);
      if (g === 'male') bmr += 5; else bmr -= 161;
      return Math.round(bmr * 1.2); 
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveStatus('');
    try {
      const newTarget = calculateTDEE(formData.weight, formData.height, formData.birthday, formData.gender);
      
      // 1. Save Profile Data
      await db.profiles.update({
        height: formData.height,
        birthday: formData.birthday,
        gender: formData.gender,
        target_calories: newTarget
      });

      // 2. Save Course Weight
      if (activeCourseId && formData.weight) {
          await db.courses.update(activeCourseId, { current_weight: formData.weight });
      }
      // 3. Instant UI Update
      setDisplayWeight(formData.weight);
      setDisplayTDEE(newTarget);
      
      setSaveStatus('Saved!');
      setTimeout(() => {
          setIsEditing(false); 
          setSaveStatus('');
      }, 1000);

    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.summaryRow}>
        <div style={styles.bioText}>
           <span style={styles.highlight}>{displayWeight} lbs</span> 
           <span style={{margin: '0 10px', color:'#555'}}>|</span>
           Target: <span style={styles.highlight}>{displayTDEE} Cal</span>
        </div>
        <button style={styles.btnEdit} onClick={() => setIsEditing(!isEditing)}>
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>

      {isEditing && (
        <div style={styles.formStack}>
           <div style={styles.inputGroup}>
             <label style={styles.label}>Current Weight (lbs) - This Course</label>
             <input style={styles.input} type="number" value={formData.weight} onChange={e => setFormData({...formData, weight: e.target.value})} />
           </div>
           
           <div style={styles.inputGroup}>
             <label style={styles.label}>Birthday</label>
             <input style={styles.input} type="date" value={formData.birthday} onChange={e => setFormData({...formData, birthday: e.target.value})} />
           </div>

           <div style={styles.inputGroup}>
             <label style={styles.label}>Height (inches)</label>
             <input style={styles.input} type="number" placeholder="e.g. 70" value={formData.height} onChange={e => setFormData({...formData, height: e.target.value})} />
           </div>

           <div style={styles.inputGroup}>
             <label style={styles.label}>Gender</label>
             <select style={styles.input} value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})}>
                <option value="male">Male</option>
                <option value="female">Female</option>
             </select>
           </div>
           
           <button style={styles.btnSave} onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : (saveStatus || 'Save & Update Target')}
           </button>
        </div>
      )}
    </div>
  );
}

export default BioSummary;